import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch, useNavigate, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { GIFT_CARD_ISSUANCE_ENABLED_LIMIT_KEY } from '@heyroot/contracts/gift-cards/issuance-switch';
import {
  DEFAULT_GRANTED_MODULE_KEYS,
  isPlatformRecordModuleKey,
} from '@heyroot/contracts/tenants/entitlements/module-keys';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import { Tabs, Tab } from '@heyroot/ui/primitives/Tabs';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import {
  ConfirmModal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { Skeleton, SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import {
  ASSISTANT_LIMIT_KEYS,
  ASSISTANT_ALLOWANCE_DEFAULTS,
} from '@heyroot/contracts/assistant/ai-allowance';
import {
  INSIGHTS_LIMIT_KEYS,
  INSIGHTS_ALLOWANCE_DEFAULTS,
  INSIGHTS_LIMIT_MAX,
  INSIGHTS_LIMIT_MIN,
} from '@heyroot/contracts/insights/ai-allowance';
import {
  fetchTenantDetail,
  updateTenantStatus,
  setEntitlement,
  fetchModuleKeys,
  queryKeys,
} from '../lib/api';
import { useIssuanceStopFor } from '../lib/gift-card-issuance-stop';
import { ACCESS_MODES, EntitlementAccessCell } from '../components/tenant/entitlement-access-cell';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';
import { NotesPanel } from '../components/notes-panel';
import { OnboardingTab } from '../components/onboarding/OnboardingTab';
import { OrganizationTab } from '../components/tenant/organization-tab';
import { UsersTab } from '../components/tenant/users-tab';
import { RolesTab } from '../components/tenant/roles-tab';
import { AuditTab } from '../components/tenant/audit-tab';
import { RegulatedProfileSection } from '../components/tenant/regulated-profile-section';

// Fix round 2 (C1): the lifecycle domain the server route actually accepts
// (tenants-router.ts statusSchema, tenants module repositories/
// provisioning.ts:224) is 'provisioning' | 'active' | 'suspended' |
// 'deactivated' -- no 'trial', no 'deleted'. This list drove a UI that
// 400'd on every single Apply; realigned to the three admin-reachable
// targets ('provisioning' is not a legal admin transition target).
//
// Final fix wave (F2): 'deactivated' removed again. The module's
// ALLOWED_FROM_STATUSES (admin-tenant-provisioning-routes.ts) has no route
// back from 'deactivated' to 'active', and no other surface reactivates a
// tenant -- offering it here is a one-way door that permanently strands a
// tenant. Spec §1/§2.6/§5/§8 also only ever name suspend/activate. The
// server's wider enum stays as harmless headroom; this list is what the UI
// offers.
const TENANT_STATUSES = ['active', 'suspended'] as const;
// full-app-audit-2026-08-29: this transition immediately cuts off a live
// tenant's access -- a misclick on the dropdown + one more click had no
// confirmation step, unlike every other destructive admin action.
const CONFIRM_STATUSES = new Set<(typeof TENANT_STATUSES)[number]>(['suspended']);

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'trial':
      return 'info';
    case 'suspended':
      return 'warning';
    case 'deleted':
      return 'danger';
    default:
      return 'neutral';
  }
}

type EntitlementRow = {
  moduleKey: string;
  accessMode: string;
  source: string;
  startsAt: string | null;
  endsAt: string | null;
  // Operator-visible AI allowances or the gift-card issuance preference.
  // Absent on older payloads or modules without editable limits.
  limits?: Record<string, unknown>;
};

export function EntitlementsSection({
  tenantId,
  entitlements,
  canWrite,
  onMutated,
}: {
  tenantId: string;
  entitlements: EntitlementRow[];
  canWrite: boolean;
  onMutated: () => void;
}) {
  const [addKey, setAddKey] = useState<string>('');
  const [addMode, setAddMode] = useState<string>('full');
  const [addError, setAddError] = useState<string | null>(null);

  const entitlementMutation = useMutation({
    mutationFn: ({
      moduleKey,
      accessMode,
      reason,
      limits,
      expectedLimits,
    }: {
      moduleKey: string;
      accessMode: string;
      reason?: string;
      limits?: Record<string, number>;
      expectedLimits?: Record<string, number>;
    }) => setEntitlement(tenantId, moduleKey, accessMode, reason, limits, expectedLimits),
    onSuccess: () => {
      onMutated();
      setAddError(null);
    },
    onError: (e) => {
      setAddError(e instanceof Error ? e.message : 'Failed to save.');
    },
  });

  // Canonical module keys come from the server (@heyroot/contracts) — no client copy to drift.
  // criticalModuleKeys drives the reason prompt below; deprecatedModuleKeys/
  // moduleLabels/moduleDependencies drive the "add a module" filtering and the
  // friendly-label + dependency-target display below.
  const { data: moduleKeysMeta } = useQuery({
    queryKey: queryKeys.moduleKeys,
    queryFn: fetchModuleKeys,
    staleTime: Infinity,
  });
  const issuanceStopFor = useIssuanceStopFor(tenantId);
  const allModuleKeys = moduleKeysMeta?.moduleKeys ?? [];
  const criticalKeys = new Set(moduleKeysMeta?.criticalModuleKeys ?? []);
  const deprecatedKeys = new Set(moduleKeysMeta?.deprecatedModuleKeys ?? []);
  const moduleLabels = moduleKeysMeta?.moduleLabels ?? {};
  const moduleDependencies = moduleKeysMeta?.moduleDependencies ?? {};
  const labelFor = (k: string) => moduleLabels[k] ?? k;
  // A platform record (`regulated_profile`, #15687) is recorded in its own section above, never
  // toggled here: drop its rows BEFORE the empty check, so a tenant whose only row is the profile
  // reads "No entitlements configured".
  const visibleEntitlements = entitlements.filter((e) => !isPlatformRecordModuleKey(e.moduleKey));
  const existingKeys = new Set(entitlements.map((e) => e.moduleKey));
  // Deprecated keys are blocked from new grants server-side (mutation engine);
  // filtering them here is defense in depth, not the access boundary. The platform-record
  // filter is the same belt-and-braces over /meta/module-keys, which already omits the key.
  const availableToAdd = allModuleKeys.filter(
    (k) => !existingKeys.has(k) && !deprecatedKeys.has(k) && !isPlatformRecordModuleKey(k),
  );

  // Critical-risk modules require a reason (mutation engine, master plan §4.4);
  // so do off/locked transitions on ANY module — kill-switch-shaped regardless
  // of risk tier (#11629). Prompt up-front; abort if the operator cancels or
  // leaves it blank.
  function submitEntitlement(
    moduleKey: string,
    accessMode: string,
    opts?: { onSuccess?: () => void },
  ) {
    let reason: string | undefined;
    const smsApproval = moduleKey === 'sms_invoice_delivery' && accessMode === 'full';
    const existing = entitlements.find((row) => row.moduleKey === moduleKey);
    const subscriptionOwned = existing?.source === 'plan' || existing?.source === 'trial';
    const isKillSwitchTransition = accessMode === 'off' || accessMode === 'locked';
    // Mirrors the engine (clearExpiryOnFullGrant): granting full over a dated override row removes its end date,
    // which is a recorded grant decision and so needs a reason.
    const clearsExpiry =
      !smsApproval &&
      accessMode === 'full' &&
      !!existing?.endsAt &&
      existing.accessMode !== 'full' &&
      existing.source === 'admin_override';
    if (criticalKeys.has(moduleKey) || isKillSwitchTransition || clearsExpiry) {
      const input = window
        .prompt(
          smsApproval
            ? `Approve "${labelFor(moduleKey)}" for this business. ${
                subscriptionOwned
                  ? 'Access follows the subscription; pausing or cancelling it disables texting.'
                  : 'This existing override keeps its current expiry and remains independent of the subscription.'
              } Record your verification details (required):`
            : criticalKeys.has(moduleKey)
              ? `"${labelFor(moduleKey)}" is a sensitive module. Enter a reason for this change (required):`
              : clearsExpiry
                ? `Turning "${labelFor(moduleKey)}" full removes its end date, so access has no expiration. Enter a reason (required):`
                : `Enter a reason for turning "${labelFor(moduleKey)}" ${accessMode} (required):`,
        )
        ?.trim();
      if (!input) {
        setAddError('A reason is required to change this entitlement.');
        return;
      }
      reason = input;
    }
    entitlementMutation.mutate(
      {
        moduleKey,
        accessMode,
        ...(reason !== undefined ? { reason } : {}),
        ...(moduleKey === 'gift-cards' && accessMode === 'full' && existing?.accessMode === 'full'
          ? {
              limits: { [GIFT_CARD_ISSUANCE_ENABLED_LIMIT_KEY]: 1 },
              expectedLimits: (existing.limits ?? {}) as Record<string, number>,
            }
          : {}),
      },
      opts,
    );
  }

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5">
      <h2 className="text-eyebrow uppercase text-muted mb-3">Entitlements</h2>
      {visibleEntitlements.length === 0 ? (
        <p className="text-body-sm text-muted">No entitlements configured.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Module</TableHeaderCell>
                <TableHeaderCell>Access Mode</TableHeaderCell>
                <TableHeaderCell>Source</TableHeaderCell>
                <TableHeaderCell>Starts</TableHeaderCell>
                <TableHeaderCell>Ends</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {visibleEntitlements.map((e) => {
                const label = moduleLabels[e.moduleKey];
                const deps = moduleDependencies[e.moduleKey] ?? [];
                return (
                  <TableRow key={e.moduleKey}>
                    <TableCell>
                      {label ? (
                        <div className="flex flex-col">
                          <span className="text-body-sm text-primary">{label}</span>
                          <span className="font-mono text-caption text-muted">{e.moduleKey}</span>
                        </div>
                      ) : (
                        <span className="font-mono text-caption">{e.moduleKey}</span>
                      )}
                      {deps.length > 0 && (
                        <div className="text-caption text-muted mt-0.5">
                          requires: {deps.map(labelFor).join(', ')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <EntitlementAccessCell
                        row={e}
                        stop={issuanceStopFor(e)}
                        canWrite={canWrite}
                        pending={entitlementMutation.isPending}
                        onChange={(mode) => submitEntitlement(e.moduleKey, mode)}
                      />
                    </TableCell>
                    <TableCell className="text-secondary text-caption">{e.source}</TableCell>
                    <TableCell className="text-muted text-caption">
                      {e.startsAt ? new Date(e.startsAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-muted text-caption">
                      {e.endsAt ? new Date(e.endsAt).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {canWrite && availableToAdd.length > 0 && (
        <div className="mt-4 flex items-center gap-2.5 flex-wrap">
          <div className="w-52">
            <Select value={addKey} onValueChange={(v) => setAddKey(v)}>
              <SelectTrigger disabled={entitlementMutation.isPending} aria-label="Module to add">
                <SelectValue placeholder="Add a module…" />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((k) => (
                  <SelectItem key={k} value={k}>
                    {labelFor(k)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Select value={addMode} onValueChange={(v) => setAddMode(v)}>
              <SelectTrigger
                disabled={entitlementMutation.isPending}
                aria-label="Access mode for new module"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              if (!addKey) return;
              submitEntitlement(addKey, addMode, { onSuccess: () => setAddKey('') });
            }}
            disabled={entitlementMutation.isPending || !addKey}
          >
            {entitlementMutation.isPending ? 'Saving…' : 'Grant'}
          </Button>
          {addError && <span className="text-body-sm text-danger-text">{addError}</span>}
        </div>
      )}
    </section>
  );
}

// #12993 — AI cost controls. Monthly limit overrides for the three AI
// entitlements (ASK-AI support questions, AI Insights questions, AI Insights
// summaries). One row per limit key, one Save per MODULE -- the apply route
// REPLACES a module's whole `limits` object when `limits` is present
// (entitlement-mutation.ts ~line 654), so Save always sends every AI limit
// key that module owns and currently has a value, never just the edited key,
// or an untouched sibling key would silently reset to its default.
type AiLimitRow = { key: string; label: string; defaultValue: number };
const AI_LIMITS_MODULES: readonly { moduleKey: string; rows: readonly AiLimitRow[] }[] = [
  {
    moduleKey: 'assistant',
    rows: [
      {
        key: ASSISTANT_LIMIT_KEYS.question,
        label: 'Ask AI support questions per month',
        defaultValue: ASSISTANT_ALLOWANCE_DEFAULTS.question,
      },
    ],
  },
  {
    moduleKey: 'insights',
    rows: [
      {
        key: INSIGHTS_LIMIT_KEYS.question,
        label: 'AI Insights questions per month',
        defaultValue: INSIGHTS_ALLOWANCE_DEFAULTS.question,
      },
      {
        key: INSIGHTS_LIMIT_KEYS.summary,
        label: 'AI Insights summaries per month',
        defaultValue: INSIGHTS_ALLOWANCE_DEFAULTS.summary,
      },
    ],
  },
];

// #14419: mirrors the server's active-window rule (starts_at <= now < ends_at).
// Returns the expiry text when the row has EXPIRED (limits cannot be set), else null.
// A not-yet-started row is not refused (its limits apply once it starts); see
// futureStartText for its note.
function entitlementInactiveReason(e: Pick<EntitlementRow, 'startsAt' | 'endsAt'>): string | null {
  if (e.endsAt && Date.parse(e.endsAt) <= Date.now()) {
    return `expired ${new Date(e.endsAt).toLocaleDateString()}`;
  }
  return null;
}

function futureStartText(e: Pick<EntitlementRow, 'startsAt'>): string | null {
  return e.startsAt && Date.parse(e.startsAt) > Date.now()
    ? new Date(e.startsAt).toLocaleDateString()
    : null;
}

function aiFieldValues(
  entitlement: EntitlementRow | undefined,
  rows: readonly AiLimitRow[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const v = entitlement?.limits?.[row.key];
    out[row.key] = typeof v === 'number' ? String(v) : '';
  }
  return out;
}

export function AiLimitsSection({
  tenantId,
  entitlements,
  canWrite,
  onMutated,
}: {
  tenantId: string;
  entitlements: EntitlementRow[];
  canWrite: boolean;
  onMutated: () => void;
}) {
  const byModule = new Map(entitlements.map((e) => [e.moduleKey, e]));
  // #14363: no row on a default-granted module resolves to 'full' (mirrors the
  // resolver), so the editor shows; the server creates the row on the first save.
  function effectiveEntitlement(moduleKey: string): Pick<EntitlementRow, 'limits'> | undefined {
    const row = byModule.get(moduleKey);
    if (row) return row;
    return (DEFAULT_GRANTED_MODULE_KEYS as ReadonlySet<string>).has(moduleKey)
      ? { limits: {} }
      : undefined;
  }

  // Same source as EntitlementsSection's own query -- react-query caches by
  // queryKey, so this is not a second network round-trip.
  const { data: moduleKeysMeta } = useQuery({
    queryKey: queryKeys.moduleKeys,
    queryFn: fetchModuleKeys,
    staleTime: Infinity,
  });
  const criticalKeys = new Set(moduleKeysMeta?.criticalModuleKeys ?? []);
  const moduleLabels = moduleKeysMeta?.moduleLabels ?? {};

  function initialFields(): Record<string, Record<string, string>> {
    const init: Record<string, Record<string, string>> = {};
    for (const mod of AI_LIMITS_MODULES) {
      init[mod.moduleKey] = aiFieldValues(byModule.get(mod.moduleKey), mod.rows);
    }
    return init;
  }
  const [fields, setFields] = useState<Record<string, Record<string, string>>>(initialFields);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // Re-seed a module's local fields whenever ITS stored limits change (e.g.
  // after a successful save invalidates + refetches the tenant). Per module:
  // both editors show for every tenant since #14363, so a save on one must not
  // discard unsaved input in the other.
  const fingerprints = AI_LIMITS_MODULES.map((mod) =>
    JSON.stringify(byModule.get(mod.moduleKey)?.limits ?? {}),
  );
  const limitsFingerprint = fingerprints.join('|');
  const seededFingerprints = useRef(fingerprints);
  useEffect(() => {
    const prev = seededFingerprints.current;
    seededFingerprints.current = fingerprints;
    setFields((current) => {
      const next = { ...current };
      AI_LIMITS_MODULES.forEach((mod, i) => {
        if (prev[i] !== fingerprints[i]) {
          next[mod.moduleKey] = aiFieldValues(byModule.get(mod.moduleKey), mod.rows);
        }
      });
      return next;
    });
  }, [limitsFingerprint]);

  const aiLimitsMutation = useMutation({
    mutationFn: ({
      moduleKey,
      limits,
      expectedLimits,
      reason,
    }: {
      moduleKey: string;
      limits: Record<string, number>;
      expectedLimits: Record<string, number>;
      reason?: string;
    }) =>
      // No accessMode (#12993 F9): the server re-reads the row's current mode,
      // so this save cannot revert a mode change made after the page loaded.
      setEntitlement(tenantId, moduleKey, undefined, reason, limits, expectedLimits),
    onSuccess: (_data, vars) => {
      setErrors((prev) => ({ ...prev, [vars.moduleKey]: null }));
      onMutated();
    },
    onError: (e, vars) => {
      setErrors((prev) => ({
        ...prev,
        [vars.moduleKey]: e instanceof Error ? e.message : 'Failed to save.',
      }));
      // #13656: someone else changed these limits. Refetch so the fields re-seed
      // from the current values (the message above tells the operator to re-apply).
      // #14419: the server found the row out of its window (its dates changed
      // since load, or it lapsed while the page was open). Refetch so the section
      // shows the row's current dates instead of a Save that keeps failing.
      const code = (e as { code?: string }).code;
      if (code === 'LIMITS_CHANGED' || code === 'ENTITLEMENT_INACTIVE') onMutated();
    },
  });

  function saveModule(moduleKey: string, rows: readonly AiLimitRow[]) {
    const entitlement = effectiveEntitlement(moduleKey);
    if (!entitlement) return;
    const values = fields[moduleKey] ?? {};
    // The apply route REPLACES the row's whole `limits`, so carry over any key
    // this editor does not own. Today the server rejects unknown keys on these
    // two modules, so none should exist -- but if one ever does, sending it
    // makes the save fail loudly (400 naming the key) instead of silently
    // deleting it.
    const ownKeys = new Set(rows.map((r) => r.key));
    const limits: Record<string, number> = {};
    for (const [key, value] of Object.entries(entitlement.limits ?? {})) {
      if (!ownKeys.has(key) && typeof value === 'number') limits[key] = value;
    }
    for (const row of rows) {
      const raw = (values[row.key] ?? '').trim();
      if (raw === '') continue;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < INSIGHTS_LIMIT_MIN || n > INSIGHTS_LIMIT_MAX) {
        setErrors((prev) => ({
          ...prev,
          [moduleKey]: `${row.label} must be a whole number from ${INSIGHTS_LIMIT_MIN} to ${INSIGHTS_LIMIT_MAX}.`,
        }));
        return;
      }
      limits[row.key] = n;
    }
    // #14363: with no explicit row the defaults already apply, so an empty save
    // would only materialize a pointless admin_override row (version bump,
    // change-log row, outbox event) that pins the module independent of the
    // default grant.
    if (!byModule.has(moduleKey) && Object.keys(limits).length === 0) {
      setErrors((prev) => ({
        ...prev,
        [moduleKey]: 'Enter a limit to save; empty fields already use the default.',
      }));
      return;
    }

    let reason: string | undefined;
    if (criticalKeys.has(moduleKey)) {
      const input = window
        .prompt(
          `"${moduleLabels[moduleKey] ?? moduleKey}" is a sensitive module. Enter a reason for this change (required):`,
        )
        ?.trim();
      if (!input) {
        setErrors((prev) => ({
          ...prev,
          [moduleKey]: 'A reason is required to change a sensitive module.',
        }));
        return;
      }
      reason = input;
    }

    setErrors((prev) => ({ ...prev, [moduleKey]: null }));
    // The limits this page loaded for the module (numeric keys only), so the
    // server can refuse the save if they changed underneath us (#13656).
    const expectedLimits: Record<string, number> = {};
    for (const [key, value] of Object.entries(entitlement.limits ?? {})) {
      if (typeof value === 'number') expectedLimits[key] = value;
    }
    aiLimitsMutation.mutate({
      moduleKey,
      limits,
      expectedLimits,
      ...(reason !== undefined ? { reason } : {}),
    });
  }

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5">
      <h2 className="text-eyebrow uppercase text-muted mb-3">AI usage limits</h2>
      <div className="flex flex-col gap-5">
        {AI_LIMITS_MODULES.map((mod) => {
          const explicit = byModule.get(mod.moduleKey);
          const entitlement = effectiveEntitlement(mod.moduleKey);
          const inactiveReason = explicit ? entitlementInactiveReason(explicit) : null;
          const startsOn = explicit ? futureStartText(explicit) : null;
          const label = moduleLabels[mod.moduleKey] ?? mod.moduleKey;
          return (
            // role=group + label: both modules' editors now render for every
            // tenant (#14363), so each "Save" needs a named container to be
            // distinguishable to assistive tech.
            <div
              key={mod.moduleKey}
              role="group"
              aria-label={label}
              className="flex flex-col gap-2.5"
            >
              {!entitlement ? (
                <p className="text-body-sm text-muted">Enable the {label} module first.</p>
              ) : inactiveReason !== null ? (
                // #14419: enforcement reads only an active row, so limits on this
                // one could never apply. No admin action edits the dates or
                // removes the row today, so this only explains (no dead Save).
                // Both modules are default-granted, so with no active row the
                // allowance falls back to its default (getEntitlementLimit -> null).
                <p className="text-body-sm text-muted">
                  The {label} grant is not active ({inactiveReason}), so usage limits cannot be set
                  on it. Until the grant&apos;s dates are corrected, the default limits apply.
                </p>
              ) : (
                <>
                  {startsOn && (
                    <p className="text-body-sm text-muted">
                      Takes effect when the grant starts on {startsOn}.
                    </p>
                  )}
                  {!explicit && (
                    <p className="text-body-sm text-muted">
                      {canWrite
                        ? 'On by default (full access); saving creates an explicit override.'
                        : 'On by default (full access).'}
                    </p>
                  )}
                  {mod.rows.map((row) => (
                    <div key={row.key} className="flex items-center gap-2.5 flex-wrap">
                      <label
                        htmlFor={`ai-limit-${row.key}`}
                        className="text-body-sm text-secondary w-64"
                      >
                        {row.label}
                      </label>
                      <Input
                        id={`ai-limit-${row.key}`}
                        type="number"
                        min={INSIGHTS_LIMIT_MIN}
                        max={INSIGHTS_LIMIT_MAX}
                        step={1}
                        placeholder={`Default: ${row.defaultValue}`}
                        value={fields[mod.moduleKey]?.[row.key] ?? ''}
                        onChange={(e) =>
                          setFields((prev) => ({
                            ...prev,
                            [mod.moduleKey]: { ...prev[mod.moduleKey], [row.key]: e.target.value },
                          }))
                        }
                        disabled={!canWrite || aiLimitsMutation.isPending}
                        className="w-32"
                      />
                    </div>
                  ))}
                  {canWrite && (
                    <div className="flex items-center gap-2.5">
                      <Button
                        variant="primary"
                        onClick={() => saveModule(mod.moduleKey, mod.rows)}
                        disabled={aiLimitsMutation.isPending}
                      >
                        {aiLimitsMutation.isPending &&
                        aiLimitsMutation.variables?.moduleKey === mod.moduleKey
                          ? 'Saving…'
                          : 'Save'}
                      </Button>
                      {errors[mod.moduleKey] && (
                        <span className="text-body-sm text-danger-text">
                          {errors[mod.moduleKey]}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Field({ label, value, span }: { label: string; value: React.ReactNode; span?: number }) {
  return (
    <div
      className="flex flex-col gap-0.5"
      style={span ? { gridColumn: `span ${span}` } : undefined}
    >
      <dt className="text-eyebrow uppercase text-muted">{label}</dt>
      <dd className="text-body-sm text-primary m-0">
        {value ?? <span className="text-muted">—</span>}
      </dd>
    </div>
  );
}

// Nine tabs, V1's order (spec §2.1 / §7.1). 'details' is gone -- Overview
// absorbs its content; Modules and Notes are promoted from in-page sections
// (still gated the same way they always were, now at the tab-list level, not
// the tab-body level -- step 4). Accounting Modules stays its OWN route
// (see ACCOUNTING_TAB_ID handling below) -- it is a 17KB page with its own
// data loading, and inlining it would triple this page's bundle for a tab
// most operators never open. Do not "finish the job" by inlining it.
const TAB_IDS = [
  'overview',
  'onboarding',
  'organization',
  'modules',
  'roles',
  'users',
  'notes',
  'audit',
  'accounting',
] as const;
type TabId = (typeof TAB_IDS)[number];
const DEFAULT_TAB: TabId = 'overview';
const ACCOUNTING_TAB_ID: TabId = 'accounting';

const TAB_LABELS: Record<TabId, string> = {
  overview: 'Overview',
  onboarding: 'Onboarding',
  organization: 'Organization',
  modules: 'Modules',
  roles: 'Roles',
  users: 'Users',
  notes: 'Notes',
  audit: 'Audit Log',
  accounting: 'Accounting Modules',
};

export function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/_auth/tenants/$tenantId' });
  const search = useSearch({ from: '/_auth/tenants/$tenantId' });
  const navigate = useNavigate();
  const { hasPerm, status: authStatus } = useAdminAuth();
  const queryClient = useQueryClient();

  // Gate the tab LIST, not the tab body (step 4) -- an operator lacking
  // USERS_READ sees eight tabs, never a Users tab that errors when clicked.
  // Modules/Accounting keep the SAME gates their in-page sections carried
  // before this restructure (moved, not rewritten); Organization/Roles/Notes/
  // Audit Log have no dedicated read bit of their own (their server routes
  // require only TENANTS_READ, which this whole page already requires), so
  // they are ungated tabs, same posture as the old Details tab.
  const visibleTabs = TAB_IDS.filter((id) => {
    if (id === 'modules') return hasPerm(PERMS.ENTITLEMENTS_READ);
    if (id === 'users') return hasPerm(PERMS.USERS_READ);
    if (id === 'accounting') return hasPerm(PERMS.ACCOUNTING_MODULES_READ);
    return true;
  });

  // Tab state lives in the URL (step 3): `search.tab` round-trips through
  // validateSearch (router.tsx), which accepts exactly these nine keys and
  // defaults/falls-back to undefined for anything else -- resolved to
  // DEFAULT_TAB here, and again if the requested tab is gated out for this
  // operator (a stale/shared link to a tab they cannot see must not render a
  // blank panel).
  const requestedTab = search.tab ?? DEFAULT_TAB;
  const activeTab: TabId = visibleTabs.includes(requestedTab) ? requestedTab : DEFAULT_TAB;
  const activeIndex = Math.max(0, visibleTabs.indexOf(activeTab));

  function goToTab(tab: TabId) {
    // Accounting Modules is a tab-shaped LINK, not a panel (see the TAB_IDS
    // comment above) -- selecting it navigates to its own route instead of
    // writing `tab=accounting` into this page's search state.
    if (tab === ACCOUNTING_TAB_ID) {
      void navigate({ to: '/tenants/$tenantId/accounting-modules', params: { tenantId } });
      return;
    }
    void navigate({
      to: '/tenants/$tenantId',
      params: { tenantId },
      search: tab === DEFAULT_TAB ? {} : { tab },
    });
  }

  // A direct/bookmarked `?tab=accounting` deep link resolves the search value
  // but never a Tabs index (accounting isn't a real panel) -- redirect it the
  // instant it's seen, same destination goToTab uses for a click.
  useEffect(() => {
    if (search.tab === ACCOUNTING_TAB_ID) {
      void navigate({
        to: '/tenants/$tenantId/accounting-modules',
        params: { tenantId },
        replace: true,
      });
    }
  }, [search.tab, navigate, tenantId]);

  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKeys.tenant(tenantId),
    queryFn: () => fetchTenantDetail(tenantId),
    enabled: authStatus === 'authenticated',
  });

  const [statusValue, setStatusValue] = useState<string>('');
  const [statusReason, setStatusReason] = useState('');
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);

  const statusMutation = useMutation({
    mutationFn: (vars: { status: string; reason: string }) =>
      updateTenantStatus(tenantId, vars.status, vars.reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) });
      setStatusError(null);
      setStatusReason('');
    },
    onError: (e) => {
      setStatusError(e instanceof Error ? e.message : 'Failed to update status.');
    },
  });

  // currentStatus is used in the status changer section below (after data guard)
  const currentStatus = statusValue || data?.tenant?.status || '';

  if (isPending) {
    return (
      <div className="flex flex-col gap-6 max-w-[900px]">
        {/* Breadcrumb skeleton */}
        <Skeleton className="h-4 w-20" />

        {/* Title skeleton */}
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>

        {/* Details section skeleton */}
        <section className="rounded-control border border-border bg-surface-elevated p-5">
          <Skeleton className="h-3 w-16 mb-4" />
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
          >
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col gap-1.5"
                style={i === 0 ? { gridColumn: 'span 2' } : undefined}
              >
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-4 w-full" style={{ width: `${55 + (i % 4) * 15}%` }} />
              </div>
            ))}
          </div>
        </section>

        {/* Entitlements section skeleton */}
        <section className="rounded-control border border-border bg-surface-elevated p-5">
          <Skeleton className="h-3 w-24 mb-3" />
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Module</TableHeaderCell>
                <TableHeaderCell>Access Mode</TableHeaderCell>
                <TableHeaderCell>Source</TableHeaderCell>
                <TableHeaderCell>Starts</TableHeaderCell>
                <TableHeaderCell>Ends</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              <SkeletonTable rows={4} cols={5} />
            </TableBody>
          </Table>
        </section>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
      >
        {error instanceof Error ? error.message : 'Failed to load tenant.'}
      </div>
    );
  }

  if (!data) {
    return <EmptyState title="Tenant not found" description="This tenant may have been removed." />;
  }

  const { tenant, entitlements, notes } = data;

  return (
    <div className="flex flex-col gap-6 max-w-[900px]">
      {/* Breadcrumb */}
      <div>
        <Link
          to="/tenants"
          className="inline-flex items-center gap-1 text-body-sm text-secondary no-underline hover:text-primary transition-colors duration-[var(--duration-fast)]"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Tenants
        </Link>
      </div>

      {/* Page title */}
      <div className="flex items-center gap-2.5">
        <h1 className="text-heading-2 text-primary">{tenant.displayName}</h1>
        <Badge variant={statusVariant(tenant.status)}>{tenant.status}</Badge>
      </div>

      <Tabs
        activeIndex={activeIndex}
        onChange={(index: number) => goToTab(visibleTabs[index] ?? DEFAULT_TAB)}
      >
        {visibleTabs.map((id) => (
          <Tab key={id} label={TAB_LABELS[id]}>
            <div className="pt-4">
              {id === 'overview' && (
                <div className="flex flex-col gap-6">
                  {/* Section: Tenant fields -- absorbs the old Details tab (step 1) */}
                  <section className="rounded-control border border-border bg-surface-elevated p-5">
                    <h2 className="text-eyebrow uppercase text-muted mb-4">Details</h2>
                    <dl
                      className="grid gap-4"
                      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
                    >
                      <Field
                        label="ID"
                        span={2}
                        value={
                          <span className="font-mono text-caption break-all">{tenant.id}</span>
                        }
                      />
                      <Field label="Slug" value={tenant.slug} />
                      <Field label="Business Name" value={tenant.businessName} />
                      <Field label="Primary Email" value={tenant.primaryEmail} />
                      <Field label="Business Type" value={tenant.businessType} />
                      <Field label="Currency" value={tenant.defaultCurrency} />
                      <Field label="Finalized" value={tenant.isFinalized ? 'Yes' : 'No'} />
                      <Field
                        label="Created"
                        value={new Date(tenant.createdAt).toLocaleDateString()}
                      />
                      <Field
                        label="Activated"
                        value={
                          tenant.activatedAt
                            ? new Date(tenant.activatedAt).toLocaleDateString()
                            : null
                        }
                      />
                      <Field
                        label="Trial Ends"
                        value={
                          tenant.trialEndsAt
                            ? new Date(tenant.trialEndsAt).toLocaleDateString()
                            : null
                        }
                      />
                    </dl>
                  </section>

                  {/* Section: Status changer */}
                  {hasPerm(PERMS.TENANT_STATUS_WRITE) && (
                    <section className="rounded-control border border-border bg-surface-elevated p-5">
                      <h2 className="text-eyebrow uppercase text-muted mb-3">Change Status</h2>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <div className="w-44">
                          <Select value={currentStatus} onValueChange={(v) => setStatusValue(v)}>
                            <SelectTrigger
                              disabled={statusMutation.isPending}
                              aria-label="Select new tenant status"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TENANT_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s.charAt(0).toUpperCase() + s.slice(1)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Input
                          value={statusReason}
                          onChange={(e) => setStatusReason(e.target.value)}
                          placeholder="Reason (required)"
                          aria-label="Reason for status change"
                          className="w-56"
                        />
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (
                              !currentStatus ||
                              currentStatus === tenant.status ||
                              !statusReason.trim()
                            )
                              return;
                            if (
                              CONFIRM_STATUSES.has(
                                currentStatus as (typeof TENANT_STATUSES)[number],
                              )
                            ) {
                              setStatusConfirmOpen(true);
                              return;
                            }
                            statusMutation.mutate({
                              status: currentStatus,
                              reason: statusReason.trim(),
                            });
                          }}
                          disabled={
                            statusMutation.isPending ||
                            !currentStatus ||
                            currentStatus === tenant.status ||
                            !statusReason.trim()
                          }
                        >
                          {statusMutation.isPending ? 'Saving…' : 'Apply'}
                        </Button>
                        {statusMutation.isSuccess && (
                          <span className="text-body-sm text-success-text">Updated.</span>
                        )}
                        {statusError && (
                          <span className="text-body-sm text-danger-text">{statusError}</span>
                        )}
                      </div>
                      {/* code-review fix (full-app-audit-2026-08-29 follow-up): a raw
                          window.confirm() was inconsistent with this same app's
                          existing ConfirmModal usage (OnboardingTab.tsx) for an
                          equally destructive action. */}
                      <ConfirmModal
                        open={statusConfirmOpen}
                        onClose={() => setStatusConfirmOpen(false)}
                        onConfirm={() => {
                          setStatusConfirmOpen(false);
                          statusMutation.mutate({
                            status: currentStatus,
                            reason: statusReason.trim(),
                          });
                        }}
                        title={`Change "${tenant.displayName}" to ${currentStatus}?`}
                        confirmLabel="Change Status"
                        destructive
                        isLoading={statusMutation.isPending}
                      >
                        <p className="text-body-sm text-secondary">
                          This immediately affects the tenant&apos;s access.
                        </p>
                      </ConfirmModal>
                    </section>
                  )}
                </div>
              )}

              {id === 'onboarding' && <OnboardingTab tenantId={tenantId} />}

              {id === 'organization' && (
                <OrganizationTab tenantId={tenantId} canWrite={hasPerm(PERMS.TENANT_ORG_WRITE)} />
              )}

              {/* Modules tab -- the old Entitlements section, moved unchanged
                  (step 5). Gate already enforced at the tab-list level above;
                  this branch only ever renders for an operator who has it. */}
              {id === 'modules' && (
                <div className="flex flex-col gap-6">
                  <RegulatedProfileSection
                    tenantId={tenantId}
                    tenantSlug={tenant.slug}
                    businessType={tenant.businessType}
                    canWrite={hasPerm(PERMS.REGULATED_PROFILE_WRITE)}
                  />
                  <EntitlementsSection
                    tenantId={tenantId}
                    entitlements={entitlements}
                    canWrite={hasPerm(PERMS.ENTITLEMENTS_WRITE)}
                    onMutated={() =>
                      queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
                    }
                  />
                  <AiLimitsSection
                    tenantId={tenantId}
                    entitlements={entitlements}
                    canWrite={hasPerm(PERMS.ENTITLEMENTS_WRITE)}
                    onMutated={() =>
                      queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
                    }
                  />
                </div>
              )}

              {id === 'roles' && (
                <RolesTab tenantId={tenantId} canWrite={hasPerm(PERMS.TENANT_USERS_MANAGE)} />
              )}

              {id === 'users' && (
                <UsersTab
                  tenantId={tenantId}
                  canWrite={hasPerm(PERMS.TENANT_USERS_MANAGE)}
                  canReissueOwnerInvite={hasPerm(PERMS.TENANTS_CREATE)}
                />
              )}

              {/* Notes tab -- the old Notes section, moved unchanged (step 5). */}
              {id === 'notes' && <NotesPanel tenantId={tenantId} notes={notes} />}

              {id === 'audit' && <AuditTab tenantId={tenantId} />}

              {/* Accounting Modules never actually renders here -- selecting it
                  navigates away (goToTab / the deep-link effect above). This
                  panel only appears for the one render frame between mount
                  and that effect firing. */}
              {id === 'accounting' && (
                <p className="text-body-sm text-muted">Opening Accounting Modules…</p>
              )}
            </div>
          </Tab>
        ))}
      </Tabs>
    </div>
  );
}
